import { View } from 'react-native';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import type { PetPurchase } from 'shared/@generated/models';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falClock } from 'shared/@generated/icons/falClock';
import { fasCheck } from 'shared/@generated/icons/fasCheck';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { PetPayBadge } from 'shared/components/petpay-badge';
import { Text } from 'shared/components/text';
import { useFormatter } from 'shared/library/use-formatter';
import { usePetpaySteps } from 'shared/library/use-petpay-steps';
import { useRoute } from 'shared/library/routes/route';

export const usePetPayCardData = (petPurchase: PetPurchase) => {
  const { t } = useTranslation('components');
  const route = useRoute();
  const { formatCurrency } = useFormatter();
  const { steps, currentStep, role } = usePetpaySteps(petPurchase);
  const otherParty = role === 'buyer' ? petPurchase.listing.user : petPurchase.buyer;
  const canAct =
    !!currentStep && (currentStep.actionRole === undefined || currentStep.actionRole === role);

  return {
    petPurchase,
    currentStep,
    canAct,
    price: formatCurrency(petPurchase.price, petPurchase.currency),
    deposit: formatCurrency(petPurchase.deposit ?? 0, petPurchase.currency),
    gender: t(
      `common:enums.gender.${petPurchase.pet.gender}${petPurchase.listing.animal ? `_${petPurchase.listing.animal}` : ''}`,
    ),
    statusLabel: !currentStep
      ? t('pet_purchase_card.completed')
      : canAct
        ? t('pet_purchase_card.next_step')
        : t('pet_purchase_card.waiting_for_other_party', { name: otherParty.display_name }),
    statusTitle: currentStep?.title ?? t('pet_purchase_card.purchase_completed'),
    statusDescription:
      currentStep?.description ?? steps[steps.length - 1]?.completedDescription ?? '',
    primaryAction:
      canAct && currentStep?.actionHref && currentStep.actionTitle
        ? { href: currentStep.actionHref, title: currentStep.actionTitle }
        : null,
    detailsHref: route('petpay.show', { petPurchase: petPurchase.uuid }),
    labels: {
      pet: t('pet_purchase_card.pet'),
      buyer: t('common:buyer'),
      seller: t('common:seller'),
      price: t('pet_purchase_card.price'),
      deposit: t('pet_purchase_modal.deposit'),
      gender: t('pet_modal.gender'),
      details: t('pet_purchase_card.view_details'),
    },
  };
};

export type PetPayCardData = ReturnType<typeof usePetPayCardData>;
export type PetPayLayoutProps = { data: PetPayCardData; horizontal: boolean };

export const PurchasePhoto = ({ data, className }: { data: PetPayCardData; className: string }) => (
  <Image
    src={data.petPurchase.pet.avatar ?? data.petPurchase.listing.avatar}
    alt={data.petPurchase.pet.name}
    containerClassName={className}
    imageClassName={'object-cover'}
    sizeType={'card'}
  />
);

export const PurchaseHeading = ({ data }: { data: PetPayCardData }) => (
  <Text
    accessibilityRole={'header'}
    aria-level={3}
    className={'text-brand-brown-dark min-w-0 flex-1 text-2xl font-bold md:text-3xl'}
    testID={`petpay-card-title-${data.petPurchase.uuid}`}
  >
    {data.petPurchase.listing.breed_name || data.petPurchase.listing.title}
  </Text>
);

export const PurchaseBadge = () => (
  <View className={'self-start'}>
    <PetPayBadge />
  </View>
);

export const PurchaseDetails = ({
  data,
  horizontal = false,
}: {
  data: PetPayCardData;
  horizontal?: boolean;
}) => (
  <View className={clsx('w-full gap-4', horizontal && 'md:flex-row')}>
    {[
      [
        { key: 'pet', label: data.labels.pet, value: data.petPurchase.pet.name },
        { key: 'gender', label: data.labels.gender, value: data.gender },
      ],
      [
        { key: 'price', label: data.labels.price, value: data.price },
        { key: 'deposit', label: data.labels.deposit, value: data.deposit },
      ],
      [
        { key: 'buyer', label: data.labels.buyer, value: data.petPurchase.buyer.display_name },
        {
          key: 'seller',
          label: data.labels.seller,
          value: data.petPurchase.listing.user.display_name,
        },
      ],
    ].map((fields, groupIndex) => (
      <View
        key={groupIndex}
        className={clsx('min-w-0 flex-row gap-4', horizontal && 'md:flex-1 md:flex-col')}
      >
        {fields.map(({ key, label, value }) => (
          <View
            key={key}
            className={'min-w-0 flex-1 gap-1'}
            testID={`petpay-card-detail-${key}-${data.petPurchase.uuid}`}
          >
            <Text className={'text-brand-brown-dark/70 text-sm'}>{label}</Text>
            <Text className={'text-brand-brown-dark text-base font-semibold md:text-lg'}>
              {value}
            </Text>
          </View>
        ))}
      </View>
    ))}
  </View>
);

export const PurchaseStatusPill = ({ data }: { data: PetPayCardData }) => (
  <View
    className={clsx(
      'max-w-full flex-row items-center gap-1.5 self-start rounded-full px-2.5 py-0.5',
      data.currentStep ? 'bg-brand-cream' : 'bg-brand-green-light',
    )}
    testID={`petpay-card-status-${data.petPurchase.uuid}`}
  >
    <Icon
      icon={!data.currentStep ? fasCheck : data.canAct ? falAngleRight : falClock}
      className={clsx(
        'size-3 shrink-0',
        data.currentStep ? 'text-brand-brown-dark' : 'text-brand-green-dark',
      )}
    />
    <Text
      className={clsx(
        'min-w-0 shrink text-sm font-semibold',
        data.currentStep ? 'text-brand-brown-dark' : 'text-brand-green-dark',
      )}
    >
      {data.statusLabel}
    </Text>
  </View>
);

export const PurchaseStatus = ({ data }: { data: PetPayCardData }) => (
  <View className={'min-w-0 gap-2'}>
    <PurchaseStatusPill data={data} />
    <Text className={'text-brand-brown-dark text-lg font-semibold'}>{data.statusTitle}</Text>
    {data.statusDescription !== '' && (
      <Text className={'text-brand-brown-dark/70 text-sm'}>{data.statusDescription}</Text>
    )}
  </View>
);

export const PurchaseActions = ({ data }: { data: PetPayCardData }) => (
  <View className={'min-w-0 gap-2'}>
    {data.primaryAction && (
      <Button
        text={data.primaryAction.title}
        href={data.primaryAction.href}
        color={'brown'}
        dense={true}
        icon={falAngleRight}
        testID={`petpay-card-action-${data.petPurchase.uuid}`}
      />
    )}
    <Button
      text={data.labels.details}
      href={data.detailsHref}
      color={data.primaryAction ? 'secondary' : 'brown'}
      icon={falAngleRight}
      testID={`petpay-card-open-${data.petPurchase.uuid}`}
    />
  </View>
);
